import React, { useMemo } from 'react';
import TimerText from 'components/timer-text/TimerText';
import Typography from 'components/typography/Typography';
import { TYPOGRAPHY_VARIANTS } from 'components/typography/typography-utils';
import { I18nNamespace } from 'constants/i18n.const';
import { LOGGING_LEVEL } from 'constants/logging.const';
import {
  DEFAULT_SHEET_OF_FRAME,
  DEFAULT_SHEET_OF_FRAME_6X20,
  FRAME_4X6_ID,
  FRAME_4X8_ID,
  FRAME_6X20_ID,
  FRAME_8X32_ID,
  FRAME_CARD_ID,
  FRAME_ITEM_PER_PAGE,
  FRAME_ITEM_PER_PAGE_VERTICAL,
  QUANTITY_DEFAULT,
} from 'constants/photo.const';
import { useBoothAppContext } from 'context/BoothAppContext';
import { useSoundContext } from 'context/SoundContext';
import { emitSeqLog } from 'functions/seq-logging.func';
import { isVideoFile } from 'helpers/image.helper';
import { isEqualVal } from 'helpers/string.helper';
import { imageUrl, resourceUrl } from 'helpers/url.helper';
import { useModal } from 'hooks/useModal';
import { useNewPhotoLifeCycleStep } from 'hooks/useNewPhotoLifeCycleStep';
import { usePageTimer } from 'hooks/usePageTimer';
import { filter, map, range, replace, size, slice } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { camerasApi } from 'store/api/camera.slice-api';
import { setSelfeAppStateAction } from 'store/features/app/selfeAppSlice';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';
import './select-frame.css';
import { Carousel } from 'components/carousel/Carousel';

function SelectFrame() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation([I18nNamespace.COMMON, I18nNamespace.PAGE]);
  const navigate = useNavigate();
  const { openErrorModal } = useModal();
  const { getNextPath } = useNewPhotoLifeCycleStep();
  const { audio } = useSoundContext();
  const { listFramePhotos, listCameras } = useBoothAppContext();

  const { boothInfo, isVerticalScreen } = useAppSelector(
    (state) => state.booth,
  );

  const { second } = usePageTimer({
    forwardFunc: () =>
      handleRedirectSelectLayout(listFramePhotos?.data?.[0]?.id)(),
    isBeforeTransaction: true,
  });

  const framePerPage = useMemo(
    () =>
      isVerticalScreen ? FRAME_ITEM_PER_PAGE_VERTICAL : FRAME_ITEM_PER_PAGE,
    [isVerticalScreen],
  );

  const handleRedirectSelectLayout =
    (frameId: number | undefined) => async () => {
      if (!frameId) return;
      if (!size(listCameras?.data)) {
        dispatch(camerasApi.util.invalidateTags([{ type: 'Camera' }]));
        openErrorModal(t('common:noCameraFound'));
        emitSeqLog?.({
          level: LOGGING_LEVEL.FATAL,
          messageTemplate: `[SelectFrame] ${t('common:noCameraFound')}`,
        });
        return;
      }

      audio?.playContinueClick?.();
      dispatch(
        setSelfeAppStateAction({
          frameId,
          quantityPicOfPerSheet: QUANTITY_DEFAULT,
          quantitySheet:
            isEqualVal(frameId, FRAME_6X20_ID) ||
            isEqualVal(frameId, FRAME_CARD_ID) ||
            isEqualVal(frameId, FRAME_8X32_ID) ||
            (boothInfo?.IsLayout820 && isEqualVal(frameId, FRAME_4X6_ID))
              ? DEFAULT_SHEET_OF_FRAME_6X20
              : DEFAULT_SHEET_OF_FRAME,
        }),
      );
      navigate(getNextPath() || '');
    };

  const listFrames = filter(listFramePhotos?.data, (item) =>
    boothInfo?.IsLayout820
      ? item?.id === FRAME_4X6_ID || item?.id === FRAME_4X8_ID
      : (boothInfo?.IsLayout620 || item?.id !== FRAME_6X20_ID) &&
        (boothInfo?.IsPrintCard || item?.id !== FRAME_CARD_ID),
  );

  return (
    <>
      <div className="select-frame">
        <TimerText second={second} />
        <Carousel
          className="frame-carousel"
          OuterProps={{ className: 'frame-page__frame-carousel' }}
          slides={map(
            range(1, Math.ceil((size(listFrames) || 0) / framePerPage) + 1),
          )}
          hiddenBtn={size(listFrames) <= framePerPage}
          RenderSlide={(_, ind) => (
            <div className="select-frame__btn-group">
              {map(
                slice(listFrames, ind * framePerPage, (ind + 1) * framePerPage),
                (item, ind) => {
                  const mediaSource = item?.medialayout
                    ? resourceUrl(item?.medialayout)
                    : imageUrl(item?.image);
                  const isVideo = isVideoFile(item?.medialayout);
                  const mediaStyle = {
                    maxHeight: { 3: '85%', 6: '90%' }[item.id],
                  };

                  return (
                    <button
                      key={item?.id}
                      type="button"
                      className="select-frame__btn"
                      onClick={handleRedirectSelectLayout(item?.id)}
                    >
                      <div className="select-frame__title-wrapper">
                        <Typography variant={TYPOGRAPHY_VARIANTS.H2}>
                          <div
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: replace(item?.name, '\\n', '\n'),
                            }}
                          />
                        </Typography>
                      </div>
                      <div className="select-frame__image-wrapper">
                        {isVideo ? (
                          <video
                            src={mediaSource}
                            autoPlay
                            loop
                            muted
                            playsInline
                            style={mediaStyle}
                          />
                        ) : (
                          <img src={mediaSource} alt="" style={mediaStyle} />
                        )}
                      </div>
                    </button>
                  );
                },
              )}
            </div>
          )}
        />
      </div>
    </>
  );
}

export default SelectFrame;
